/**
 * Main App Component
 * Orchestrates the audio recorder, OCR uploader, and transcript display
 * Layout: 2x2 grid (Speech-to-text | OCR) / (Shared Transcript Display)
 * Compatible with Node.js 16.20.2
 */

import React, { useState } from 'react';
import AudioRecorder from './components/AudioRecorder';
import OCRUploader from './components/OCRUploader';
import TranscriptDisplay from './components/TranscriptDisplay';
import './App.css';

function App() {
  const [transcript, setTranscript] = useState({
    text: '',
    metadata: null,
  });
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingSource, setProcessingSource] = useState(null);

  /**
   * Handle transcript received from either API (Speech-to-text or OCR)
   */
  const handleTranscriptReceived = (apiResponse) => {
    console.log('Transcript received:', apiResponse);
    
    // Determine the source and set appropriate metadata
    let metadata = {
      source: apiResponse.metadata?.source || 'Unknown'
    };

    if (apiResponse.metadata?.source === 'OCR') {
      // OCR response
      metadata = {
        ...metadata,
        fileName: apiResponse.metadata?.fileName,
        fileSize: apiResponse.metadata?.fileSize,
        uploadedAt: apiResponse.metadata?.uploadedAt,
        jobId: apiResponse.metadata?.jobId,
        tablesExtracted: apiResponse.metadata?.tablesExtracted,
        processingDetails: apiResponse.metadata?.processingDetails,
        partialSuccess: apiResponse.metadata?.partialSuccess,
        downloadError: apiResponse.metadata?.downloadError
      };
    } else {
      // Speech-to-text response
      metadata = {
        ...metadata,
        request_id: apiResponse.metadata?.request_id,
        language_code: apiResponse.metadata?.language_code,
        language_probability: apiResponse.metadata?.language_probability,
        timestamps: apiResponse.metadata?.timestamps,
        diarized_transcript: apiResponse.metadata?.diarized_transcript
      };
    }

    setTranscript({
      text: apiResponse.text || '',
      metadata
    });

    // Clear processing state after a brief delay
    setTimeout(() => {
      setIsProcessing(false);
      setProcessingSource(null);
    }, 500);
  };

  /**
   * Handle when API processing starts
   */
  const handleProcessingStart = (source) => {
    setIsProcessing(true);
    setProcessingSource(source);
  };

  /**
   * Handle errors
   */
  const handleError = (errorMessage) => {
    setError(errorMessage);
    setIsProcessing(false);
    setProcessingSource(null);
  };

  return (
    <div className="app">
      <div className="container">
        {error && (
          <div className="error-alert">
            <span className="error-icon">⚠️</span>
            <div className="error-content">
              <strong>Error:</strong> {error}
              <button
                className="error-close"
                onClick={() => setError(null)}
                aria-label="Close error message"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        <div className="app-grid">
          <div className="grid-item audio-recorder-wrapper">
            <AudioRecorder
              onTranscriptReceived={handleTranscriptReceived}
              onError={handleError}
              onProcessingStart={() => handleProcessingStart('speech-to-text')}
              isDisabled={isProcessing && processingSource === 'ocr'}
            />
          </div>

          <div className="grid-item ocr-uploader-wrapper">
            <OCRUploader
              onTranscriptReceived={handleTranscriptReceived}
              isEnabled={!isProcessing || processingSource !== 'speech-to-text'}
            />
          </div>

          <div className="grid-item transcript-display-wrapper">
            <TranscriptDisplay 
              transcript={transcript} 
              isLoading={isProcessing}
              processingSource={processingSource}
            />
          </div>
        </div>

        <footer className="app-footer">
          <p>
            🔐 Your data is processed by Sarvam AI. No data is stored on our
            servers.
          </p>
        </footer>
      </div>
    </div>
  );
}

export default App;